<div class="page" *ngIf="stage !== 'meeting'">
  <span class="stripe" aria-hidden="true"></span>

  <header class="brand">
    <img src="assets/SCRAII_LoginLogo.svg" alt="SCRAII" class="brand-logo" />
  </header>

  <main class="lobby" *ngIf="stage === 'lobby'">
    <div class="intro">
      <ng-container *ngIf="detailsLoading; else details">
        <span class="skeleton skeleton-title" aria-hidden="true"></span>
        <p class="sub">Choose your video and audio options</p>
        <span class="skeleton skeleton-meta" aria-hidden="true"></span>
        <span class="sr-only" role="status">Loading meeting details</span>
      </ng-container>

      <ng-template #details>
        <h1>{{ preview?.title || "Join meeting" }}</h1>
        <p class="sub">Choose your video and audio options</p>
        <ul class="meta" *ngIf="preview && (whenLabel || preview.hostEmail || preview.publicCode || !preview.canJoin)">
          <li *ngIf="whenLabel">
            <mat-icon fontSet="material-icons-outlined">schedule</mat-icon>
            <span>{{ whenLabel }}</span>
          </li>
          <li *ngIf="preview.hostEmail">
            <mat-icon fontSet="material-icons-outlined">person</mat-icon>
            <span>{{ preview.hostEmail }}</span>
          </li>
          <li *ngIf="preview.publicCode">
            <mat-icon fontSet="material-icons-outlined">tag</mat-icon>
            <span>{{ preview.publicCode }}</span>
          </li>
          <!-- In the meta row rather than under it, so it costs width, not height. -->
          <li class="status" *ngIf="!preview.canJoin">
            <mat-icon fontSet="material-icons-outlined">hourglass_empty</mat-icon>
            <span>This meeting is not open yet.</span>
          </li>
        </ul>
      </ng-template>
    </div>

    <div class="cards">
      <section class="lj-card video-card" aria-label="Video preview">
        <div class="preview-frame">
          <video #cam autoplay playsinline [muted]="true" [class.hidden]="!cameraOn"></video>

          <!-- Before the states so `.device-note ~ .preview-state` can make room for it. -->
          <p class="device-note" *ngIf="cameraError" role="alert">
            <mat-icon fontSet="material-icons-outlined">{{ cameraNoteIcon }}</mat-icon>
            <span>{{ cameraError }}</span>
          </p>

          <div class="preview-state" *ngIf="cameraOn && cameraStarting" role="status">
            <span class="spinner" aria-hidden="true"></span>
            <span>Starting camera…</span>
          </div>

          <div class="preview-state" *ngIf="!cameraOn">
            <span
              class="avatar"
              *ngIf="displayName.trim()"
              [style.background]="avatar.background"
              [style.color]="avatar.color"
              aria-hidden="true">{{ initials }}</span>
            <span>{{ cameraUnavailable ? "Camera preview isn't available here" : "Your camera is off" }}</span>
          </div>

          <span class="preview-name" *ngIf="displayName.trim() && cameraOn && !cameraStarting">{{ displayName.trim() }}</span>
        </div>

        <div class="video-bar">
          <button type="button" class="switch-row" role="switch" [attr.aria-checked]="cameraOn" (click)="toggleCamera()" aria-label="Camera">
            <mat-icon fontSet="material-icons-outlined">videocam</mat-icon>
            <span class="switch" [class.on]="cameraOn" aria-hidden="true"><i></i></span>
          </button>
          <button
            type="button"
            class="pill-btn"
            *ngIf="blurSupported"
            [class.active]="blur || sideView === 'effects'"
            [attr.aria-pressed]="sideView === 'effects'"
            (click)="openSide('effects')">
            <mat-icon fontSet="material-icons-outlined">auto_awesome</mat-icon>
            <span>Background filters</span>
          </button>
        </div>
      </section>

      <section class="lj-card side-card" [class.panel-open]="sideView !== 'audio'" aria-label="Audio and join options">
        <div class="side-inner">
          <div class="side-head" *ngIf="sideView !== 'audio'">
            <button type="button" class="icon-btn" (click)="openSide('audio')" aria-label="Back to audio options">
              <mat-icon fontSet="material-icons-outlined">arrow_back</mat-icon>
            </button>
            <h2>{{ sideView === "devices" ? "Device settings" : "Background filters" }}</h2>
          </div>

          <div class="side-body" [ngSwitch]="sideView">
            <div *ngSwitchCase="'devices'" class="side-pad">
              <app-meeting-device-settings
                variant="card"
                [headings]="false"
                [mics]="devices.mics"
                [speakers]="devices.speakers"
                [cameras]="devices.cameras"
                [audioInputId]="audioInputId"
                [audioOutputId]="audioOutputId"
                [videoInputId]="videoInputId"
                [canChooseSpeaker]="canChooseSpeaker"
                [audioDisabled]="audioMode === 'none'"
                (micChange)="chooseMic($event)"
                (speakerChange)="chooseSpeaker($event)"
                (cameraChange)="chooseCamera($event)"
                (done)="openSide('audio')">
              </app-meeting-device-settings>
            </div>

            <div *ngSwitchCase="'effects'" class="side-pad">
              <app-meeting-device-settings
                [showDevices]="false"
                [showEffects]="true"
                [headings]="false"
                [blurSupported]="blurSupported"
                [blur]="blur"
                (blurChange)="setBlur($event)">
              </app-meeting-device-settings>
              <p class="note" *ngIf="!cameraOn">Turn your camera on to preview the effect.</p>
            </div>

            <div *ngSwitchDefault class="audio-options" role="radiogroup" aria-label="Audio">
              <div class="option" [class.selected]="audioMode === 'computer'">
                <div class="option-head">
                  <label class="radio">
                    <input type="radio" name="audioMode" [checked]="audioMode === 'computer'" (change)="setAudioMode('computer')" />
                    <span class="dot" aria-hidden="true"></span>
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">laptop</mat-icon>
                    <span class="option-label">Computer audio</span>
                  </label>
                  <button
                    type="button"
                    class="icon-btn gear"
                    (click)="openSide('devices')"
                    matTooltip="Device settings"
                    aria-label="Device settings">
                    <mat-icon fontSet="material-icons-outlined">settings</mat-icon>
                  </button>
                </div>
                <div class="mic-row" *ngIf="audioMode === 'computer'">
                  <button type="button" class="switch-row" role="switch" [attr.aria-checked]="!muted" (click)="toggleMic()" aria-label="Microphone">
                    <mat-icon fontSet="material-icons-outlined">mic_none</mat-icon>
                    <span class="switch" [class.on]="!muted" aria-hidden="true"><i></i></span>
                  </button>
                  <span class="level" #meter aria-hidden="true" [class.muted]="muted">
                    <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                  </span>
                </div>
              </div>

              <div class="option" [class.selected]="audioMode === 'none'">
                <div class="option-head">
                  <label class="radio">
                    <input type="radio" name="audioMode" [checked]="audioMode === 'none'" (change)="setAudioMode('none')" />
                    <span class="dot" aria-hidden="true"></span>
                    <mat-icon fontSet="material-icons-outlined" aria-hidden="true">volume_off</mat-icon>
                    <span class="option-label">Don't use audio</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="join-form">
            <label for="displayName">Your name</label>
            <input
              id="displayName"
              class="field"
              [(ngModel)]="displayName"
              maxlength="80"
              placeholder="Enter your name"
              autocomplete="name"
              (keydown.enter)="join()" />

            <ng-container *ngIf="!codeFromLink || !preview">
              <label for="meetingCode">Meeting code</label>
              <input id="meetingCode" class="field" [(ngModel)]="code" placeholder="ABCD-2345" (keydown.enter)="join()" />
            </ng-container>

            <button type="button" class="join-btn" (click)="join()" [disabled]="loading || !canJoin">
              <span class="spinner" *ngIf="loading" aria-hidden="true"></span>
              {{ loading ? "Joining…" : "Join the Call" }}
            </button>
            <p class="error" *ngIf="error" role="alert">
              <mat-icon fontSet="material-icons-outlined">error_outline</mat-icon>
              <span>{{ error }}</span>
            </p>
          </div>

          <p class="hint" *ngIf="joinHint && !loading">{{ joinHint }}</p>
        </div>
      </section>
    </div>
  </main>

  <main class="left-screen" *ngIf="stage === 'left'">
    <div class="lj-card left-card">
      <span class="left-icon" [class.warn]="leftInfo?.reason === 'error'">
        <mat-icon fontSet="material-icons-outlined">{{ leftIcon }}</mat-icon>
      </span>
      <h1>{{ leftTitle }}</h1>
      <p>{{ leftText }}</p>
      <p class="duration" *ngIf="leftDuration">Meeting duration {{ leftDuration }}</p>
      <p class="summary-note" *ngIf="summaryStatus" [attr.aria-busy]="summaryBusy">{{ summaryStatus }}</p>
      <div class="left-actions">
        <button type="button" class="join-btn" *ngIf="canRejoin" (click)="rejoin()" [disabled]="loading || summaryBusy">
          {{ loading ? "Rejoining…" : "Rejoin" }}
        </button>
        <button type="button" class="secondary-btn" (click)="dismiss()" [disabled]="summaryBusy">Dismiss</button>
      </div>
      <p class="summary-note" *ngIf="closeBlocked" role="status">You can close this tab now.</p>
      <p class="error" *ngIf="error" role="alert">
        <mat-icon fontSet="material-icons-outlined">error_outline</mat-icon>
        <span>{{ error }}</span>
      </p>
    </div>
  </main>

  <footer class="footer">Copyright © 2026 All Rights Reserved by <b>Data Unveil</b></footer>
</div>

<app-meeting-room
  *ngIf="stage === 'meeting' && joined"
  [title]="joined.title"
  [publicCode]="preview?.publicCode || code.trim()"
  [transcriptionEnabled]="joined.transcriptionEnabled"
  [transcriptionError]="joined.transcriptionError"
  [meetingResponse]="joined.meetingResponse"
  [attendeeResponse]="joined.attendeeResponse"
  [startMuted]="muted"
  [startCameraOn]="cameraOn"
  [devices]="joinOptions"
  [displayName]="displayName.trim()"
  [hostEmail]="preview?.hostEmail || ''"
  [isHost]="isHost"
  (left)="onLeft($event)">
</app-meeting-room>
